import React, { useState } from "react";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

const ExportData = ({ 
  tableData = [], 
  fileName = "export", 
  sheetName = "Sheet1",
  disabled = false 
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Export to Excel (XLSX)
  const exportToExcel = () => {
    if (!tableData || tableData.length === 0) {
      toast.error("No data available to export");
      return;
    }

    setIsExporting(true);
    try {
      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      // Convert JSON data to worksheet
      const ws = XLSX.utils.json_to_sheet(tableData);
      
      // Set column widths
      const columnWidths = Object.keys(tableData[0] || {}).map(key => ({
        wch: Math.max(
          key.length + 2,
          ...tableData.map(row => 
            String(row[key] || '').length
          ).slice(0, 100) // Check first 100 rows for performance
        )
      }));
      ws['!cols'] = columnWidths;
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      
      // Generate file name with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const fullFileName = `${fileName}_${timestamp}.xlsx`;
      
      // Write and download
      XLSX.writeFile(wb, fullFileName);
      
      toast.success(`Exported ${tableData.length} records to Excel`);
      setShowDropdown(false);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export data to Excel");
    } finally {
      setIsExporting(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!tableData || tableData.length === 0) {
      toast.error("No data available to export");
      return;
    }

    setIsExporting(true);
    try {
      // Get headers
      const headers = Object.keys(tableData[0]);
      
      // Create CSV content
      const csvContent = [
        // Header row
        headers.join(','),
        // Data rows
        ...tableData.map(row => 
          headers.map(header => {
            const value = row[header];
            // Escape values that contain commas, quotes, or newlines
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(',')
        )
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const timestamp = new Date().toISOString().split('T')[0];
      const fullFileName = `${fileName}_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', fullFileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${tableData.length} records to CSV`);
      setShowDropdown(false);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      toast.error("Failed to export data to CSV");
    } finally {
      setIsExporting(false);
    }
  };

  // Quick export (default to Excel)
  const quickExport = () => {
    exportToExcel();
  };

  return (
    <div className="relative">
      {/* Quick Export Button */}
      <div className="flex items-center gap-1">
        <button
          onClick={quickExport}
          disabled={disabled || isExporting || !tableData?.length}
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-l-lg font-medium transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isExporting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Download size={16} />
              Export
            </>
          )}
        </button>
        
        {/* Dropdown Toggle */}
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={disabled || isExporting || !tableData?.length}
          className="bg-green-500 hover:bg-green-600 text-white px-2 py-2 rounded-r-lg border-l border-green-600 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown Menu */}
      {showDropdown && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setShowDropdown(false)}
          />
          <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Export Format
              </p>
            </div>
            
            <button
              onClick={exportToExcel}
              disabled={isExporting}
              className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors disabled:opacity-50"
            >
              <FileSpreadsheet size={18} className="text-green-600" />
              <div>
                <div className="font-medium">Excel (.xlsx)</div>
                <div className="text-xs text-gray-500">Best for data analysis</div>
              </div>
            </button>
            
            <button
              onClick={exportToCSV}
              disabled={isExporting}
              className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors disabled:opacity-50"
            >
              <FileText size={18} className="text-blue-600" />
              <div>
                <div className="font-medium">CSV (.csv)</div>
                <div className="text-xs text-gray-500">Universal format</div>
              </div>
            </button>

            <div className="px-4 py-2 border-t border-gray-100 mt-1">
              <p className="text-xs text-gray-500">
                {tableData?.length || 0} records available
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ExportData;